import React, { useState, useEffect } from 'react';
import { Tour } from '@economic/gaudi';

import { t, embedList, useLocalization } from '../../../shared/components/l10n/l10n';
import { useEContext } from '../../../shared/components/EContext';

function useDelayedOnMount(fn: Function, ms: number) {
    useEffect(() => {
        let isMounted = true;

        setTimeout(() => {
            if (isMounted) {
                fn();
            }
        }, ms);

        return () => {
            isMounted = false;
        };
    }, []);
}
type Props = {
    onDone: (doneAllSteps: boolean) => void;
};

export default function IntroductionTour({ onDone }: Props) {
    const { routes, identity } = useEContext();

    const [showTour, setShowTour] = useState(false);

    // Delay introduced due to tour sometimes not showing up
    useDelayedOnMount(() => {
        setShowTour(true);
    }, 2000);

    const loadingTexts = useLocalization(textIds);

    if (!identity || loadingTexts) {
        return null;
    }
    if (!identity.session.features.homeTour || identity.currentAgreement.showGettingStarted) {
        return null;
    }
    if (!showTour) {
        return null;
    }

    const hasSettings = !!routes && routes.hasOwnProperty('/settings');

    const steps = !hasSettings
        ? [
              <Tour.Step selector="[name=chat]" title={t`HOME_TOUR_CHAT_TITLE`}>
                  {embedList(t`HOME_TOUR_CHAT_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=ecopedia]" title={t`HOME_TOUR_ECOPEDIA_TITLE`}>
                  {embedList(t`HOME_TOUR_ECOPEDIA_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=settings]" title={t`HOME_TOUR_SETTINGS_TITLE`}>
                  {t`HOME_TOUR_SETTINGS_CONTENT`}
              </Tour.Step>,
          ]
        : [
              <Tour.Step selector="[name=home]" title={t`HOME_TOUR_HOME_TITLE`} position="bottom">
                  {embedList(t`HOME_TOUR_HOME_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=sales]" title={t`HOME_TOUR_SALES_TITLE`}>
                  {embedList(t`HOME_TOUR_SALES_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=accounting]" title={t`HOME_TOUR_ACCOUNTING_TITLE`}>
                  {embedList(t`HOME_TOUR_ACCOUNTING_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=reports]" title={t`HOME_TOUR_REPORTS_TITLE`}>
                  {embedList(t`HOME_TOUR_REPORTS_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=market]" title={t`HOME_TOUR_MARKET_TITLE`}>
                  {embedList(t`HOME_TOUR_MARKET_CONTENT`)}
              </Tour.Step>,

              <Tour.Step selector="[name=chat]" title={t`HOME_TOUR_CHAT_TITLE`}>
                  {embedList(t`HOME_TOUR_CHAT_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=ecopedia]" title={t`HOME_TOUR_ECOPEDIA_TITLE`}>
                  {embedList(t`HOME_TOUR_ECOPEDIA_CONTENT`)}
              </Tour.Step>,
              <Tour.Step selector="[name=settings]" title={t`HOME_TOUR_ALLSETTINGS_TITLE`}>
                  {embedList(t`HOME_TOUR_ALLSETTINGS_CONTENT`)}
              </Tour.Step>,
          ];

    function done() {
        onDone(true);
    }

    function close() {
        setShowTour(false);
        onDone(false);
    }

    return (
        <Tour
            continuous
            autoStart
            localization={{
                back: t`GUIDED_TOUR_BUTTON_BACK`,
                close: t`GUIDED_TOUR_BUTTON_CLOSE`,
                last: t`GUIDED_TOUR_BUTTON_DONE`,
                next: t`GUIDED_TOUR_BUTTON_NEXT`,
            }}
            onComplete={done}
            disableCloseOnEsc
            disableOverlayClose
            onClose={close}>
            {React.Children.map(steps, (step, index) => {
                return React.cloneElement(step, {
                    key: index,
                    position: 'bottom',
                });
            })}
        </Tour>
    );
}

const textIds = [
    'HOME_TOUR_CHAT_TITLE',
    'HOME_TOUR_CHAT_CONTENT',
    'HOME_TOUR_ECOPEDIA_TITLE',
    'HOME_TOUR_ECOPEDIA_CONTENT',
    'HOME_TOUR_SETTINGS_TITLE',
    'HOME_TOUR_SETTINGS_CONTENT',

    'HOME_TOUR_HOME_TITLE',
    'HOME_TOUR_HOME_CONTENT',
    'HOME_TOUR_SALES_TITLE',
    'HOME_TOUR_SALES_CONTENT',
    'HOME_TOUR_ACCOUNTING_TITLE',
    'HOME_TOUR_ACCOUNTING_CONTENT',
    'HOME_TOUR_REPORTS_TITLE',
    'HOME_TOUR_REPORTS_CONTENT',
    'HOME_TOUR_MARKET_TITLE',
    'HOME_TOUR_MARKET_CONTENT',
    'HOME_TOUR_ALLSETTINGS_TITLE',
    'HOME_TOUR_ALLSETTINGS_CONTENT',
];
